import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';
import BoardUtility from '@/scripts/board-utility';
import { FundPoolsService } from '@/scripts/services/mdf/fund-pools-service';
import { MDFFilterService } from '@/scripts/services/quick-filters/mdf';

export const MDFBudgetBoardServiceMixin = {
    data() {
        return {
            FilterKey: {
                FUNDTYPES: 'fundTypes',
                VISIBLE: 'visibleToPartners',
                EXPIRATION: 'expirationDates',
            },

            VISIBILITY: {
                Yes: true,
                No: false
            },

            EXPIRATION: {
                ACTIVE: 'Funds that haven’t expired',
                EXPIRED: 'Funds that have expired'
            }
        }
    },
    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },
    },

    methods: {
        refreshEvent() {
            EventBus.$emit('budget:update');
        },

        // Board Query Formatter
        getFilterApplied({
            typeConditions,
            visibleConditions,
            expirationConditions,
            searchFilter,
        }) {
            const queryFilters = [];
            const result = [];
            if (searchFilter) {
                queryFilters.push(searchFilter);
            }
            if (typeConditions && typeConditions.length) {
                result.push({ Type: { $in: typeConditions } });
            }
            if (visibleConditions && visibleConditions.length) {
                result.push({ IsVisibleToPartners: { $in: visibleConditions } });
            }

            if (expirationConditions && expirationConditions.length) {
                result.push({ $or: expirationConditions});
            }

            const stringifiedCode = result.map((obj) => JSON.stringify(obj)).join(',');

            result.length > 0 && queryFilters.push(stringifiedCode);

            const currentFilter =
                queryFilters.length > 0 ? `where=[${queryFilters.join(', ')}]` : '';
            return currentFilter;
        },

        getFilterParam(options) {
            let searchFilter = null;
            let currentFilter = '';

            if (options.search) {
                searchFilter = `{"$Title$": {"$like": "${encodeURIComponent(`%${options.search}%`)}"}}`;
            }

            if (options.quickFilters.active) {
                let typeConditions = [];
                let visibleConditions = [];
                let expirationConditions = [];
                options.quickFilters.activeFilters.forEach(({ key, name }) => {
                    if (key && name) {
                        switch (key) {
                            case this.FilterKey.FUNDTYPES:
                                typeConditions.push(name);
                                break;
                            case this.FilterKey.VISIBLE:
                                visibleConditions.push(this.VISIBILITY[name]);
                                break;
                            case this.FilterKey.EXPIRATION:
                                {
                                    if (name === this.EXPIRATION.EXPIRED) {
                                        expirationConditions.push({ ExpirationDate: { $lte: this.$moment().startOf('day').toISOString() } });
                                    }

                                    if (name === this.EXPIRATION.ACTIVE) {
                                        expirationConditions.push({ ExpirationDate: { $gt: this.$moment().startOf('day').toISOString() } });
                                        expirationConditions.push({ ExpirationDate: { $eq: null} });
                                    }
                                }
                                break;
                        }
                    }
                });
                currentFilter = this.getFilterApplied({
                    typeConditions,
                    visibleConditions,
                    expirationConditions,
                    searchFilter,
                });
            } else if (!options.quickFilters.active) {
                const queryFilters = [];
                const viewQueryFilter = this.getViewQueryFilter(options.filters);
                if (viewQueryFilter) {
                    queryFilters.push(viewQueryFilter);
                }

                if (searchFilter) {
                    queryFilters.push(searchFilter);
                }

                currentFilter = queryFilters.length > 0 ? `where=[${queryFilters.join(', ')}]` : '';
            }

            return currentFilter;
        },

        getViewQueryFilter(filters) {
            if (filters.value.length > 0) {
                const viewQueryFilters = filters.value.map(this.mapFilter);

                if (filters.operation === 'Or') {
                    return `{ "$or": [${viewQueryFilters.join(', ')}]}`;
                } else {
                    return `{ "$and": [${viewQueryFilters.join(', ')}]}`;
                }
            }
            return null;
        },

        mapFilter(filter) {
            switch (filter.key) {
                case 'title':
                    return BoardUtility.getCustomFieldStringFilterQuery('Title', filter);
                case 'budget':
                    return BoardUtility.getCustomFieldAmountFilterQuery('Budget', filter);
                case 'fund-type':
                    return BoardUtility.getCustomFieldStringFilterQuery('Type', filter);
                case 'visibility':
                    return BoardUtility.getCustomFieldBooleanFilterQuery('IsVisibleToPartners', filter);
                default:
                    return null;
            }
        },

        getPageParam(options) {
            return `offset=${options.itemsPerPage * (options.page - 1)}`;
        },

        getTopParam(options) {
            return `limit=${options.itemsPerPage}`;
        },

        getQueryParams(options) {
            return [
                this.getFilterParam(options),
                this.getPageParam(options),
                this.getTopParam(options),
                'order=[["UpdatedAt", "desc"]]',
            ].join('&');
        },

        // Requests

        getBudgetFilterCounts(queryParams = '{}') {
            return new Promise((resolve, reject) => {
                MDFFilterService.getFundPoolCounts(this.accountId,queryParams)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(reject)
            })
        },

        getAgencyBudgets({ options = null }) {
            const requestConfig = this.getQueryParams(options);
            return new Promise((resolve, reject) => {
                FundPoolsService.getAll(this.accountId, requestConfig)
                    .then(({ data }) => {
                        resolve({
                        items: data.data.map(this.mapBudgets),
                        count: data.count,
                    })})
                    .catch(reject);
            });
        },

        getAgencyBudgetsVisibleCount() {
            const requestConfig = `where=[${JSON.stringify({ IsVisibleToPartners: true })}]`;
            return new Promise((resolve, reject) => {
                FundPoolsService.getAll(this.accountId, requestConfig)
                    .then(({ data }) => {
                        resolve({
                        count: data.count,
                    })})
                    .catch(reject);
            });
        },

        getAgencyBudgetsNotVisibleCount() {
            const requestConfig = `where=[${JSON.stringify({ IsVisibleToPartners: false })}]`;
            return new Promise((resolve, reject) => {
                FundPoolsService.getAll(this.accountId, requestConfig)
                    .then(({ data }) => {
                        resolve({
                        count: data.count,
                    })})
                    .catch(reject);
            });
        },

        getAgencyBudgetsExpiredCount() {
            const requestConfig = `where=[${JSON.stringify({ ExpirationDate: { $lte: this.$moment().startOf('day').toISOString() } })}]`;
            return new Promise((resolve, reject) => {
                FundPoolsService.getAll(this.accountId, requestConfig)
                    .then(({ data }) => {
                        resolve({
                        count: data.count,
                    })})
                    .catch(reject);
            });
        },

        getAgencyBudgetsNotExpiredCount() {
            const requestConfig = `where=[${JSON.stringify({ $or: [{ ExpirationDate: { $gt: this.$moment().startOf('day').toISOString() } }, { ExpirationDate: { $eq: null} }]})}]`;
            return new Promise((resolve, reject) => {
                FundPoolsService.getAll(this.accountId, requestConfig)
                    .then(({ data }) => {
                        resolve({
                        count: data.count,
                    })})
                    .catch(reject);
            });
        },

        addNewBudget({ budget }) {
            const payload = this.formatBudgetToPayload(budget);

            return new Promise((resolve, reject) => {
                FundPoolsService.post(payload, this.accountId)
                    .then(resolve)
                    .catch(reject);
            });
        },

        updateBudget({ id, budget }) {
            const payload = this.formatBudgetToPayload(budget);

            return new Promise((resolve, reject) => {
                FundPoolsService.put(id, this.accountId, payload)
                    .then(resolve)
                    .catch(reject);
            });
        },

        deleteItem({ id }) {
            FundPoolsService.delete(id, this.accountId)
                .then(() => {
                    this.$helpers.showSuccessNotification('MDF budget deleted successfully.');
                    this.refreshEvent();
                })
                .catch(({response}) => {
                    this.$helpers.showFailNotification(
                        `${
                            response.status === 400
                                ? response.data.data
                                : 'An error occurred while deleting your MDF budget.'
                        }`, { error: response }
                    );
                });
        },

        // Mappers
        mapBudgets(budget) {
            const {
                Id,
                Title,
                Budget,
                Type,
                IsVisibleToPartners,
                AvailabilityDate,
                ExpirationDate,
                AccountId,
                CreatedAt,
                UpdatedAt
            } = budget;
            
            return {
                id: Id,
                title: Title,
                budget: {
                    currencySymbol: '$',
                    amount: Budget,
                    color: '#2ADA2B',
                },
                type: Type,
                visibility: {
                    value: IsVisibleToPartners,
                    size: 'large',
                    text: 'YES',
                },
                availability: AvailabilityDate && this.$moment(AvailabilityDate).local(),
                expiration: ExpirationDate && this.$moment(ExpirationDate).local(),
                accountId: AccountId,
                createdAt:  this.$moment(CreatedAt).local(),
                updatedAt: this.$moment(UpdatedAt).local(),
            }
        },

        formatBudgetToPayload(budget) {
            const { title, amount, type, visibility, availability, expiration } = budget;

            const payload = {
                Title: title,
                Budget: Number(amount),
                Type: type.name,
                IsVisibleToPartners: visibility,
                AvailabilityDate: this.$moment(availability.date).startOf('day').toISOString(),
                ExpirationDate: expiration.date && this.$moment(expiration.date).startOf('day').toISOString(),
                AccountId: this.accountId
            };

            return payload
        },

        // Factories
        createDeleteMDFModal(item, confirmFunction) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Budget',
                        action: () => confirmFunction(item),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const content = '<p>Please keep in mind that once the budget is deleted, it can\'t be recovered.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Are you sure you want to delete the budget?',
                        content,
                        actions,
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    }
                );
            };
        },

    }
}